import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency, formatDateShort } from '../utils/format';
import { Calendar, FileText, Wallet, Filter, CreditCard, Download, X, Search, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

export default function TumOdemeler() {
  const { currentUser, kullanicilar } = useApp();
  const [odemeler, setOdemeler] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [bankalar, setBankalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('hepsi');
  const [selectedTip, setSelectedTip] = useState('hepsi');
  const [selectedBanka, setSelectedBanka] = useState('tumu');
  const [arama, setArama] = useState('');
  const [groupBy, setGroupBy] = useState('gun');
  
  // Tarih aralığı
  const [baslangicTarihi, setBaslangicTarihi] = useState('');
  const [bitisTarihi, setBitisTarihi] = useState('');
  
  // Debounced arama - 300ms gecikme ile
  const debouncedArama = useDebounce(arama, 300);

  // Aya Göre seçildiğinde sadece görsel gruplama yap, tarih filtresini değiştirme
  const handleGroupByChange = (value) => {
    setGroupBy(value);
    // Tarih filtresini değiştirme - liste tüm verileri göstersin
  };

  // Mevcut ay bilgisi (özet hesaplaması için)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const firstDay = `${year}-${month.toString().padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const lastDayStr = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
    return { firstDay, lastDayStr };
  };

  useEffect(() => {
    loadBankalar();
  }, []);

  useEffect(() => {
    loadOdemeler();
  }, [filter, selectedTip, selectedBanka, debouncedArama, baslangicTarihi, bitisTarihi, groupBy]);

  const loadBankalar = async () => {
    try {
      const res = await api.getBankalar();
      // Bankalar listesini tekil olarak göster (her banka adı sadece bir kez)
      const uniqueBankalar = [];
      const seenNames = new Set();
      res.data.forEach(banka => {
        if (!seenNames.has(banka.ad)) {
          seenNames.add(banka.ad);
          uniqueBankalar.push(banka);
        }
      });
      setBankalar(uniqueBankalar);
    } catch (error) {
      console.error('Error loading banks:', error);
    }
  };

  // Kullanıcı kısa adı
  const getKullaniciKisaAd = (kullaniciId) => {
    if (!kullaniciId || !kullanicilar) return '';
    const kullanici = kullanicilar.find(k => k.id === kullaniciId);
    if (!kullanici) return '';
    const parcalar = kullanici.ad.split(' ');
    if (parcalar.length >= 2) {
      return `${parcalar[0][0]}${parcalar[1][0]}`.toUpperCase();
    }
    return parcalar[0]?.substring(0, 2).toUpperCase() || '';
  };

  const loadOdemeler = async () => {
    try {
      setLoading(true);
      const params = {
        subeId: currentUser.rol === 'sube_sorumlusu' ? currentUser.subeId : undefined,
        kullaniciId: currentUser.rol === 'veri_giris' ? currentUser.id : undefined,
        rol: currentUser.rol,
        durum: filter === 'hepsi' ? undefined : filter,
        tip: selectedTip === 'hepsi' ? undefined : selectedTip,
        bankaId: selectedBanka !== 'tumu' ? selectedBanka : undefined,
        arama: debouncedArama || undefined,
        baslangicTarihi: baslangicTarihi || undefined,
        bitisTarihi: bitisTarihi || undefined
      };

      const [odemelerRes, ozetRes] = await Promise.all([
        api.getTumOdemeler(params),
        api.getTumOdemelerOzet({
          subeId: params.subeId,
          kullaniciId: params.kullaniciId,
          rol: params.rol,
          baslangicTarihi: params.baslangicTarihi,
          bitisTarihi: params.bitisTarihi
        })
      ]);

      const odemelerData = odemelerRes.data;
      setOdemeler(odemelerData);
      
      // Özet hesaplaması için veri seti belirle
      let ozetVerileri = odemelerData;
      
      // Eğer "Aya Göre" seçili ve tarih filtresi yoksa, mevcut ayın verilerini kullan
      if (groupBy === 'ay' && !baslangicTarihi && !bitisTarihi) {
        const { firstDay, lastDayStr } = getCurrentMonthRange();
        ozetVerileri = odemelerData.filter(o => {
          const vade = o.vadeTarihi;
          return vade >= firstDay && vade <= lastDayStr;
        });
      }
      
      // Özeti filtrelenmiş ödemelerden hesapla
      const hesaplananOzet = {
        toplamBekleyenCek: ozetVerileri.filter(o => o.tip === 'cek' && o.durum === 'beklemede').reduce((sum, o) => sum + (o.tutar || 0), 0),
        toplamBekleyenDiger: ozetVerileri.filter(o => o.tip === 'diger' && o.durum === 'beklemede').reduce((sum, o) => sum + (o.tutar || 0), 0),
        toplamBekleyenKredi: ozetVerileri.filter(o => o.tip === 'kredi' && o.durum === 'beklemede').reduce((sum, o) => sum + (o.tutar || 0), 0),
        toplamBekleyenKrediKarti: ozetVerileri.filter(o => o.tip === 'kredikarti').reduce((sum, o) => sum + (o.tutar || 0), 0),
        toplamBekleyenLC: ozetVerileri.filter(o => o.tip === 'lc' && o.durum === 'beklemede').reduce((sum, o) => sum + (o.tutar || 0), 0),
        toplamBekleyen: ozetVerileri.filter(o => o.durum === 'beklemede' || o.tip === 'kredikarti').reduce((sum, o) => sum + (o.tutar || 0), 0),
        yaklasanCekSayisi: ozetVerileri.filter(o => o.tip === 'cek').length,
        yaklasanOdemeSayisi: ozetVerileri.filter(o => o.tip === 'diger').length,
        yaklasanKrediSayisi: ozetVerileri.filter(o => o.tip === 'kredi').length,
        yaklasanKrediKartiSayisi: ozetVerileri.filter(o => o.tip === 'kredikarti').length,
        yaklasanLCSayisi: ozetVerileri.filter(o => o.tip === 'lc').length,
        yaklasanToplamTutar: ozetVerileri.reduce((sum, o) => sum + (o.tutar || 0), 0)
      };
      
      setOzet(hesaplananOzet);
    } catch (error) {
      console.error('Error loading odemeler:', error);
      toast.error('Ödemeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOzetKartClick = (tip) => {
    setSelectedTip(tip);
  };

  const handleExcelExport = () => {
    const params = new URLSearchParams();
    if (baslangicTarihi) params.append('baslangicTarihi', baslangicTarihi);
    if (bitisTarihi) params.append('bitisTarihi', bitisTarihi);
    if (selectedTip !== 'hepsi') params.append('tip', selectedTip);
    if (filter !== 'hepsi') params.append('durum', filter);
    
    window.open(`${BACKEND_URL}/api/export/tum-odemeler/excel?${params.toString()}`, '_blank');
  };

  const handlePdfExport = () => {
    const params = new URLSearchParams();
    if (baslangicTarihi) params.append('baslangicTarihi', baslangicTarihi);
    if (bitisTarihi) params.append('bitisTarihi', bitisTarihi);
    if (selectedTip !== 'hepsi') params.append('tip', selectedTip);
    if (filter !== 'hepsi') params.append('durum', filter);
    
    window.open(`${BACKEND_URL}/api/export/tum-odemeler/pdf?${params.toString()}`, '_blank');
  };

  const handleRefresh = () => {
    loadOdemeler();
    toast.success('Veriler yenilendi');
  };

  const clearFilters = () => {
    setBaslangicTarihi('');
    setBitisTarihi('');
    setSelectedTip('hepsi');
    setSelectedBanka('tumu');
    setArama('');
    setFilter('hepsi');
  };

  const getDurumBadge = (durum) => {
    const styles = {
      beklemede: 'bg-amber-100 text-amber-800 border border-amber-300',
      odendi: 'bg-success/10 text-success border border-success'
    };
    const labels = { beklemede: 'Beklemede', odendi: 'Ödendi' };
    return (
      <span className={`px-2 py-1 text-xs font-medium ${styles[durum]}`}>
        {labels[durum]}
      </span>
    );
  };

  const getTipBadge = (tip) => {
    if (tip === 'cek') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Çek
        </span>
      );
    }
    if (tip === 'kredi') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-300 flex items-center gap-1">
          <CreditCard className="h-3 w-3" />
          Kredi
        </span>
      );
    }
    if (tip === 'kredikarti') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-pink-100 text-pink-800 border border-pink-300 flex items-center gap-1">
          <CreditCard className="h-3 w-3" />
          K.Kartı
        </span>
      );
    }
    if (tip === 'lc') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-300 flex items-center gap-1">
          <FileText className="h-3 w-3" />
          LC
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300 flex items-center gap-1">
        <Wallet className="h-3 w-3" />
        Diğer
      </span>
    );
  };

  // Ödemeleri grupla
  const groupOdemeler = () => {
    if (groupBy === 'gun') {
      const grouped = {};
      odemeler.forEach(odeme => {
        const date = odeme.vadeTarihi;
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(odeme);
      });
      return grouped;
    } else {
      const grouped = {};
      odemeler.forEach(odeme => {
        const [year, month] = odeme.vadeTarihi.split('-');
        const key = `${year}-${month}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(odeme);
      });
      return grouped;
    }
  };

  const formatGroupHeader = (key) => {
    if (groupBy === 'gun') {
      return formatDateShort(key);
    } else {
      const [year, month] = key.split('-');
      const aylar = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
      return `${aylar[parseInt(month)]} ${year}`;
    }
  };

  const getGroupTotal = (items) => items.reduce((sum, o) => sum + o.tutar, 0);

  const groupedOdemeler = groupOdemeler();
  const sortedKeys = Object.keys(groupedOdemeler).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="tum-odemeler-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center gap-2 md:gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Tüm Ödemeler</h1>
            <p className="text-xs md:text-base text-textSecondary mt-1">Çekler, krediler ve diğer ödemelerin birleşik görünümü</p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 text-textSecondary hover:text-primary hover:bg-surfaceSubtle rounded-full transition-colors"
            title="Yenile"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExcelExport}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            Excel
          </Button>
          <Button
            onClick={handlePdfExport}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="export-pdf-btn"
          >
            <Download className="h-4 w-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* Özet Kartları - Tıklanabilir */}
      {ozet && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 md:gap-3">
          <button
            onClick={() => handleOzetKartClick('cek')}
            data-testid="ozet-cek"
            className={`bg-surface border p-3 md:p-4 text-left transition-all hover:shadow-md ${
              selectedTip === 'cek' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-border'
            }`}
          >
            <p className="text-xs text-textSecondary mb-1">Bekleyen Çekler</p>
            <p className="text-base md:text-xl font-mono font-bold text-blue-600">{formatCurrency(ozet.toplamBekleyenCek)}</p>
          </button>
          <button
            onClick={() => handleOzetKartClick('diger')}
            data-testid="ozet-diger"
            className={`bg-surface border p-3 md:p-4 text-left transition-all hover:shadow-md ${
              selectedTip === 'diger' ? 'border-purple-500 ring-2 ring-purple-200' : 'border-border'
            }`}
          >
            <p className="text-xs text-textSecondary mb-1">Bekleyen Diğer</p>
            <p className="text-base md:text-xl font-mono font-bold text-purple-600">{formatCurrency(ozet.toplamBekleyenDiger)}</p>
          </button>
          <button
            onClick={() => handleOzetKartClick('kredi')}
            data-testid="ozet-kredi"
            className={`bg-surface border p-3 md:p-4 text-left transition-all hover:shadow-md ${
              selectedTip === 'kredi' ? 'border-green-500 ring-2 ring-green-200' : 'border-border'
            }`}
          >
            <p className="text-xs text-textSecondary mb-1">Bekleyen Krediler</p>
            <p className="text-base md:text-xl font-mono font-bold text-green-600">{formatCurrency(ozet.toplamBekleyenKredi || 0)}</p>
          </button>
          <button
            onClick={() => handleOzetKartClick('kredikarti')}
            data-testid="ozet-kredikarti"
            className={`bg-surface border p-3 md:p-4 text-left transition-all hover:shadow-md ${
              selectedTip === 'kredikarti' ? 'border-pink-500 ring-2 ring-pink-200' : 'border-border'
            }`}
          >
            <p className="text-xs text-textSecondary mb-1">Kredi Kartları</p>
            <p className="text-base md:text-xl font-mono font-bold text-pink-600">{formatCurrency(ozet.toplamBekleyenKrediKarti || 0)}</p>
          </button>
          <button
            onClick={() => handleOzetKartClick('lc')}
            data-testid="ozet-lc"
            className={`bg-surface border p-3 md:p-4 text-left transition-all hover:shadow-md ${
              selectedTip === 'lc' ? 'border-cyan-500 ring-2 ring-cyan-200' : 'border-border'
            }`}
          >
            <p className="text-xs text-textSecondary mb-1">Bekleyen LC</p>
            <p className="text-base md:text-xl font-mono font-bold text-cyan-600">{formatCurrency(ozet.toplamBekleyenLC || 0)}</p>
          </button>
          <button
            onClick={() => handleOzetKartClick('hepsi')}
            data-testid="ozet-toplam"
            className={`bg-surface border p-3 md:p-4 text-left transition-all hover:shadow-md ${
              selectedTip === 'hepsi' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-border'
            }`}
          >
            <p className="text-xs text-textSecondary mb-1">Toplam Bekleyen</p>
            <p className="text-base md:text-xl font-mono font-bold text-amber-600">{formatCurrency(ozet.toplamBekleyen)}</p>
          </button>
          <div className="bg-surface border border-border p-3 md:p-4">
            <p className="text-xs text-textSecondary mb-1">Dönem İçi Vadesi Dolan</p>
            <p className="text-base md:text-xl font-mono font-bold text-error">{formatCurrency(ozet.yaklasanToplamTutar)}</p>
            <p className="text-xs text-textSecondary mt-1">
              {(ozet.yaklasanCekSayisi || 0) + (ozet.yaklasanOdemeSayisi || 0) + (ozet.yaklasanKrediSayisi || 0) + (ozet.yaklasanKrediKartiSayisi || 0) + (ozet.yaklasanLCSayisi || 0)} adet
            </p>
          </div>
        </div>
      )}

      {/* Filtreler */}
      <div className="bg-surface border border-border p-3 md:p-4">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <Filter className="h-4 w-4 md:h-5 md:w-5 text-textSecondary hidden sm:block" />
          
          {/* Arama */}
          <div className="relative flex-1 min-w-[100px] max-w-[150px]">
            <Search className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
            <Input
              type="text"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              placeholder="Ara..."
              className="pl-8 md:pl-9 w-full h-8 md:h-9 text-sm"
              data-testid="arama-input"
            />
          </div>
          
          {/* Tarih Aralığı */}
          <div className="flex items-center gap-1 md:gap-2">
            <Calendar className="h-4 w-4 text-textSecondary hidden md:block" />
            <Input
              type="date"
              value={baslangicTarihi}
              onChange={(e) => setBaslangicTarihi(e.target.value)}
              className="w-[110px] md:w-[130px] h-8 md:h-9 text-sm"
              data-testid="baslangic-tarihi"
            />
            <span className="text-xs md:text-sm text-textSecondary">-</span>
            <Input
              type="date"
              value={bitisTarihi}
              onChange={(e) => setBitisTarihi(e.target.value)}
              className="w-[110px] md:w-[140px] h-8 md:h-9 text-sm"
              data-testid="bitis-tarihi"
            />
          </div>

          {/* Banka Filtresi */}
          <Select value={selectedBanka} onValueChange={setSelectedBanka}>
            <SelectTrigger className="w-[100px] md:w-[120px] h-8 md:h-9 text-sm" data-testid="banka-select">
              <SelectValue placeholder="Banka" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tumu">Tüm Bankalar</SelectItem>
              {bankalar.map((banka) => (
                <SelectItem key={banka.id} value={banka.id}>{banka.ad}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Durum Filtresi */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[90px] md:w-[110px] h-8 md:h-9 text-sm" data-testid="durum-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hepsi">Hepsi</SelectItem>
              <SelectItem value="beklemede">Beklemede</SelectItem>
              <SelectItem value="odendi">Ödendi</SelectItem>
            </SelectContent>
          </Select>

          {/* Gruplama */}
          <Select value={groupBy} onValueChange={handleGroupByChange}>
            <SelectTrigger className="w-[85px] md:w-[100px] h-8 md:h-9 text-sm" data-testid="group-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="gun">Güne Göre</SelectItem>
                <SelectItem value="ay">Aya Göre</SelectItem>
              </SelectContent>
            </Select>

          {/* Filtreleri Temizle */}
          {(baslangicTarihi || bitisTarihi || selectedTip !== 'hepsi' || filter !== 'hepsi' || arama || selectedBanka !== 'tumu') && (
            <Button
              onClick={clearFilters}
              variant="ghost"
              size="sm"
              className="text-textSecondary hover:text-textPrimary"
              data-testid="clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              Temizle
            </Button>
          )}
        </div>
        
        {/* Aktif Tip Filtresi */}
        {selectedTip !== 'hepsi' && (
          <div className="mt-3 pt-3 border-t border-border">
            <span className="text-sm text-textSecondary">Aktif Filtre: </span>
            <span className="text-sm font-medium text-primary">
              {selectedTip === 'cek' && 'Sadece Çekler'}
              {selectedTip === 'diger' && 'Sadece Diğer Ödemeler'}
              {selectedTip === 'kredi' && 'Sadece Krediler'}
              {selectedTip === 'kredikarti' && 'Sadece Kredi Kartları'}
              {selectedTip === 'lc' && 'Sadece LC Ödemeleri'}
            </span>
          </div>
        )}
      </div>

      {/* Ödeme Listesi */}
      {odemeler.length === 0 ? (
        <div className="bg-surface border border-border p-12 text-center">
          <Calendar className="h-16 w-16 text-textSecondary mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-textSecondary">Seçilen kriterlere göre ödeme bulunamadı</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedKeys.map((key) => (
            <div key={key} className="bg-surface border border-border overflow-hidden">
              {/* Grup Başlığı */}
              <div className="bg-surfaceSubtle border-b border-border px-4 py-3 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="font-heading font-bold text-textPrimary">
                    {formatGroupHeader(key)}
                  </span>
                  <span className="text-xs text-textSecondary">
                    ({groupedOdemeler[key].length} ödeme)
                  </span>
                </div>
                <div className="font-mono font-bold text-primary">
                  {formatCurrency(getGroupTotal(groupedOdemeler[key]))}
                </div>
              </div>
              
              {/* Grup İçeriği */}
              <div className="overflow-x-auto">
                <table className="w-full" data-testid={`odemeler-table-${key}`}>
                  <thead className="bg-surfaceSubtle/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-textSecondary uppercase">Tip</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-textSecondary uppercase">Açıklama</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-textSecondary uppercase">Tutar</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-textSecondary uppercase">Vade</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-textSecondary uppercase">Durum</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-textSecondary uppercase">Giren</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {groupedOdemeler[key].map((odeme, index) => (
                      <tr key={odeme.id || index} className="hover:bg-surfaceSubtle/30">
                        <td className="px-4 py-3 whitespace-nowrap">{getTipBadge(odeme.tip)}</td>
                        <td className="px-4 py-3 text-sm text-textPrimary">
                          <div className="font-medium">{odeme.aciklama}</div>
                          {odeme.kategori && odeme.tip === 'diger' && (
                            <div className="text-xs text-textSecondary mt-0.5">{odeme.kategori}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-right font-bold text-textPrimary">
                          {formatCurrency(odeme.tutar)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-textPrimary">
                          {formatDateShort(odeme.vadeTarihi)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{getDurumBadge(odeme.durum)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {odeme.girenKullaniciId && (
                            <span className="text-xs font-medium text-textSecondary bg-surfaceSubtle px-1.5 py-0.5 rounded">
                              {getKullaniciKisaAd(odeme.girenKullaniciId)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Genel Toplam */}
      {odemeler.length > 0 && (
        <div className="bg-primary/10 border-2 border-primary p-3 md:p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <p className="text-xs md:text-sm text-textSecondary">Filtrelenen Ödemeler Toplamı</p>
            <p className="text-xs text-textSecondary mt-1">{odemeler.length} adet ödeme</p>
          </div>
          <p className="text-xl md:text-2xl font-mono font-bold text-primary">
            {formatCurrency(odemeler.reduce((sum, o) => sum + o.tutar, 0))}
          </p>
        </div>
      )}
    </div>
  );
}
